import * as React from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import WaitingForApprovalStep from "./WaitingForApprovalStep";
import ApprovedStep from "./ApprovedStep";
import CompletedStep from "./CompletedStep";
import AccountingApprovalStep from "./AccountingApprovalStep";
import InvoicedStep from "./InvoicedStep";
import SNEvent from "./interfaces/SNEvent";
import NewStep from "./NewStep";
import { Box, Button, Grid2, Tab, Tabs, TextField, Typography } from "@mui/material";
import { CustomTabPanel } from "../../Common/UI/components/CustomTabPanel";
import LabeledTextField from "./components/LabeledTextField";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import NumbersIcon from "@mui/icons-material/Numbers";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptIcon from "@mui/icons-material/Receipt";
import styles from "./styles/AuditTrail.module.css";

import style from "./styles/CreateEvent.module.css";
import { theme } from "../../Common/UI/styles/theme";

import PersonIcon from "@mui/icons-material/Person";
import CreditCard from "@mui/icons-material/CreditCard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SignalWifiStatusbar4BarIcon from "@mui/icons-material/SignalWifiStatusbar4Bar";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PinDropIcon from "@mui/icons-material/PinDrop";
import EmailIcon from "@mui/icons-material/Email";
import CommentIcon from "@mui/icons-material/Comment";
import { Business, ContactMail, LocationOn } from "@mui/icons-material";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import { DatePicker } from "@mui/x-date-pickers";
import { VendorSelectionDialog } from "./components/VendorSelectionDialog";
import dayjs from "dayjs";
import Vendor from "./interfaces/Vendor";
import styleStepper from "./styles/HorizontalLinearStepper.module.css";
import { useEffect, useRef, useState } from "react";
import { useFormError } from "../../Common/UI/hooks/useFormError";
import useURLBuilder from "../../Common/UI/hooks/useURLBuilder";
import { config } from "../../authConfig";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import EditedInvoice from "./interfaces/EditedInvoice";
import PaidStep from "./PaidStep";
import Message from "./interfaces/Message";
import DiscussionComment from "./components/DiscussionComment";
import RedButton from "../../Common/UI/components/RedButton";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import { ColDef, ICellRendererParams, RowClassParams, RowClickedEvent, RowHeightParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridLoadingOverlay } from "../../Common/UI/components/GridLoadingOverlay";
import SaveDateButton from "./SaveDateButton";
import { CommentDialog } from "./components/CommentDialog";

export enum StepsEnum {
  New = 0,
  WaitingApproval = 1,
  Approved = 2,
  Completed = 3,
  AccountingApproval = 4,
  Invoiced = 5,
  Paid = 6,
}

export enum StateEnum {
  New = 1,
  WaitingApproval = 2,
  PendingCorrection = 3,
  Approved = 4,
  Completed = 5,
  PendingAccountingApproval = 10,
  Invoiced = 6,
  Paid = 7,
  Cancelled = 9,
  PendingContributionCorrection = 11,
  Archived = 12,
}

interface ContributionFormError {
  salesContribution: string;
  donationContribution: string;
}

const EventStateToUIStep = function (event: SNEvent | null) {
  var step = StepsEnum.New;

  switch (event?.stateId) {
    case StateEnum.New:
      step = StepsEnum.New;
      break;
    case StateEnum.WaitingApproval:
      step = StepsEnum.WaitingApproval;
      break;
    case StateEnum.PendingCorrection:
      step = StepsEnum.New;
      break;
    case StateEnum.Approved:
      step = StepsEnum.Approved;
      break;
    case StateEnum.Completed:
      step = StepsEnum.Completed;
      break;
    case StateEnum.Invoiced:
      step = StepsEnum.Invoiced;
      break;
    case StateEnum.Paid:
      step = StepsEnum.Paid;
      break;
    case StateEnum.PendingAccountingApproval:
      step = StepsEnum.AccountingApproval;
      break;
    case StateEnum.PendingContributionCorrection:
      step = StepsEnum.Completed;
      break;
    case StateEnum.Cancelled:
      step = StepsEnum.Completed;
      break;
  }

  return step;
};

export default function HorizontalLinearStepper({ event, setEvent }: { event: SNEvent; setEvent: React.Dispatch<React.SetStateAction<SNEvent>> }) {
  const { instance } = useMsal();
  const { isAdmin, isViewOnly } = useIsAdminAndViewOnly();
  const [activeStep, setActiveStep] = React.useState(0);
  const [steps, setSteps] = useState<string[]>(["New", "Waiting Approval", "Approved", "Completed", "Accounting Approval", "Invoiced", "Paid"]);

  const [value, setValue] = useState(0);
  const [editedInvoice, setEditedInvoice] = useState<EditedInvoice | null>(null);
  const [showVendorSelectDialog, setShowVendorSelectDialog] = React.useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessageText, setNewMessageText] = useState<string>("");
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false);
  const [isLoadingAuditTrail, setIsLoadingAuditTrail] = useState<boolean>(false);
  const { formErrors, isSubmittable, handleInputValidation } = useFormError<ContributionFormError>({ salesContribution: "", donationContribution: "" });
  const [vendor, setVendor] = useState<Vendor | undefined>();

  useEffect(() => {
    if (event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid) {
      const getVendor = async () => {
        const vendorData = await fetchVendorFromNS();
        setVendor(vendorData);
      };
      getVendor();
    }
  }, [event.vendor?.companyName]);

  const handleStep = (stepNumber: number) => {
    setActiveStep(stepNumber);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleClose = () => setShowVendorSelectDialog(false);

  const handleVendorSelected = function (vendor: Vendor) {
    if (!!event) {
      event.vendor = vendor;
      setEvent(event);
    }
  };

  const fetchVendorFromNS = async () => {
    try {
      const url = `${config.rootAPIUrl}/api/vendor/search?VendorName=${encodeURIComponent(event.vendor?.companyName || "")}&code=${config.rootFunctionCode}`;
      console.log("Fetching vendor from NetSuite with URL:", url);
      const response = await execute("GET", `${url}`);
      if (response !== undefined) {
        if (response.length > 0) {
          const vendorNetSuite: Vendor = response[0];
          return vendorNetSuite;
        } else {
          console.error("No vendor found with the provided name.");
        }
      }
    } catch (error: any) {
      console.error(error);
      throw new Error(error);
    }
  };

  const [reportTotal, setReportTotal] = useState<number | null>(null);
  const [auditTrail, setAuditTrail] = useState<any>(null);
  const [salesContribution, setSalesContribution] = useState<string>(event.salesContribution?.toString() || "");
  const [donationContribution, setDonationContribution] = useState<string>(event.donationContribution?.toString() || "");
  const [donationTotal, setDonationTotal] = useState<number | null>(null);
  const urlBuilder = useURLBuilder();
  const execute = useFetchMsal();

  useEffect(() => {
    if (event.stateId == StateEnum.Cancelled) {
      setSteps(["New", "Waiting Approval", "Approved", "Cancelled", "Accounting Approval", "Invoiced", "Paid"]);
    }
    const uiStep = EventStateToUIStep(event);
    setActiveStep(uiStep);
  }, [event]);

  useEffect(() => {
    if (event && event.id && event.stateId && (event.stateId == StateEnum.PendingAccountingApproval || event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid)) {
      savedReport();
    }
  }, [event]);

  // handle table resizing when the window is resized
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const savedReport = async function () {
    try {
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.savedReportTotal}`, {
        id: event.id!,
      });
      const response = await execute("GET", url);

      setReportTotal(response.total);
    } catch (error) {
      console.error(error);
      throw new Error("Report total pull failed!");
    }
  };

  const getAuditTrail = async () => {
    try {
      setIsLoadingAuditTrail(true);
      if (!event.id) return;
      const url = config.rootAPIList?.auditTrail.replace("{id}", event.id.toString());
      const response = await execute("GET", `${config.rootAPIUrl}${url}`);
      if (response !== undefined) {
        setAuditTrail(response);
      }
    } catch (error: any) {
      console.error(error);
      throw new Error(error);
    } finally {
      setIsLoadingAuditTrail(false);
    }
  };

  const modifiedInvoice = async function () {
    try {
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.invoiceInfo}`, {
        id: event.id!,
      });

      const response = await execute("GET", url);
      setEditedInvoice(response);
    } catch (error) {
      console.error(error);
      throw new Error("Modified invoice pull failed!");
    }
  };

  useEffect(() => {
    //pull the invoice that was generated
    if (event && event.id && event.stateId && (event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid)) {
      modifiedInvoice();
    }

    //change selected tab to Invoice info
    if (event && event.id && event.stateId && (event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid || event.stateId == StateEnum.PendingAccountingApproval)) {
      setValue(1);
    }

    if (event && event.id && event.stateId && event.stateId == StateEnum.PendingContributionCorrection) {
      setValue(0);
    }
  }, [event]);

  useEffect(() => {
    const donation = calculateTotalDonationValue(reportTotal, parseFloat(salesContribution), parseFloat(donationContribution));
    setDonationTotal(donation);
  }, [reportTotal, salesContribution, donationContribution]);

  const calculateTotalDonationValue = (reportTotal: number | null, contribution: number | undefined, addlDonation: number): number => {
    if (reportTotal != null && contribution != undefined) {
      return (reportTotal + contribution) * 0.15 + addlDonation;
    }
    return 0;
  };

  const fetchMessages = async () => {
    try {
      setIsLoadingComments(true);
      if (!event.id) return;
      const url = config.rootAPIList?.getMessages.replace("{id}", event.id.toString());
      const response = await execute("GET", `${config.rootAPIUrl}${url}`);
      if (response !== undefined) {
        setMessages(response);
      }
    } catch (error: any) {
      console.error(error);
      throw new Error(error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [event]);

  useEffect(() => {
    getAuditTrail();
  }, [event]);

  const handleSendMessage = async () => {
    setIsLoadingComments(true);
    const activeAccount: null | AccountInfo = instance.getActiveAccount();

    const url = `${config.rootAPIUrl}${config.rootAPIList?.createMessage}`;
    let newMessage: Message = {
      author: activeAccount?.name,
      roleId: isAdmin ? 2 : 1,
      text: newMessageText,
      createdAt: new Date().toUTCString(),
      eventId: event.id,
    };
    const response = await execute("POST", url, newMessage);
    setNewMessageText("");
    fetchMessages();
  };
  const [isSending, setSending] = useState<boolean>(false);
  const [showCommentPopup, setShowCommentPopup] = useState<boolean>(false);
  const handleCloseComments = () => setShowCommentPopup(false);

    const handleArchive = async function (comment: string) {
      try {
        setSending(true);
        setEvent((prevEvent) => {
          prevEvent.comment = comment;
          return prevEvent;
        });
        const url = `${config.rootAPIUrl}${config.rootAPIList?.archive}`;
        const response = await execute("POST", url, event);
        setEvent(response);
        setSending(false);
        handleStep(StepsEnum.New);
      } catch (error) {
        console.error(error);
        throw new Error("Event archival failed!");
      }
    };

  const gridRef = useRef<AgGridReact>(null);

  const colsDef: ColDef[] = [
    { field: "id", headerName: "Id" },
    { field: "date", headerName: "Date" },
    { field: "user", headerName: "User" },
    { field: "action", headerName: "Action" },
    {
      field: "property",
      headerName: "Property",
    },
    {
      field: "oldValue",
      headerName: "Old Value",
    },
    {
      field: "newValue",
      headerName: "New Value",
    },
  ];

  const onInitialDataRender = async () => {
    await gridRef.current?.api.setColumnFilterModel("state", {
      operator: "AND",
      conditions: [
        { type: "notEqual", filter: "Closed" },
        { type: "notEqual", filter: "Cancelled" },
      ],
    });

    gridRef.current?.api.onFilterChanged();

    gridRef.current?.api.applyColumnState({
      state: [{ colId: "id", sort: "desc" }],
      defaultState: { sort: null },
    });
  };

  const eventDatePickerDisabled = () => {
    if (isAdmin && (event?.stateId === StateEnum.New || event?.stateId === StateEnum.WaitingApproval || event?.stateId === StateEnum.PendingCorrection || event?.stateId === StateEnum.Approved)) {
      return false;
    }

    return event?.stateId !== StateEnum.New ? true : false;
  };

  return (
    <>
      <Box sx={{ paddingBottom: "40px", marginLeft: "-8px" }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
      {activeStep === StepsEnum.New ? (
        <NewStep handleStep={handleStep} event={event} setEvent={setEvent} />
      ) : activeStep === StepsEnum.WaitingApproval ? (
        <WaitingForApprovalStep handleStep={handleStep} event={event} setEvent={setEvent} />
      ) : activeStep === StepsEnum.Approved ? (
        <ApprovedStep handleStep={handleStep} event={event} setEvent={setEvent} setSteps={setSteps} isSubmittable={salesContribution.length > 0 && donationContribution.length > 0 && isSubmittable} />
      ) : activeStep === StepsEnum.AccountingApproval ? (
        <AccountingApprovalStep handleStep={handleStep} event={event} setEvent={setEvent} setReportTotal={setReportTotal} donationTotal={donationTotal} setEditedInvoice={setEditedInvoice} />
      ) : activeStep === StepsEnum.Invoiced ? (
        <InvoicedStep handleStep={handleStep} setEditedInvoice={setEditedInvoice} event={event} setEvent={setEvent} />
      ) : activeStep === StepsEnum.Paid ? (
        <PaidStep handleStep={handleStep} />
      ) : (
        <></>
      )}

      <Box sx={{ width: "100%" }}>
        <Grid2 container justifyContent={"space-between"}>
          <Grid2 size={"auto"}>
            <Tabs value={value} onChange={handleTabChange} aria-label="">
              <Tab label="Event Info" id={`simple-tab-1`} aria-controls={`simple-tabpanel-1`} sx={{ fontSize: "1rem" }} />
              <Tab
                disabled={!(!!event.stateId && (event.stateId == StateEnum.PendingAccountingApproval || event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid))}
                label="Invoice Info"
                id={`simple-tab-2`}
                aria-controls={`simple-tabpanel-2`}
                sx={{ fontSize: "1rem" }}
              />
              <Tab label="Discussion" id={`simple-tab-3`} aria-controls={`simple-tabpanel-3`} sx={{ fontSize: "1rem" }} />
              <Tab label="Audit" id={`simple-tab-4`} aria-controls={`simple-tabpanel-4`} sx={{ fontSize: "1rem" }} />
            </Tabs>
          </Grid2>
          <Grid2 size={"auto"}>
            {activeStep === StepsEnum.Completed && <CompletedStep handleStep={handleStep} event={event} setEvent={setEvent} setReportTotal={setReportTotal} setValue={setValue} />}
          </Grid2>
        </Grid2>

        {/* panel 1 */}
        <CustomTabPanel value={value} index={0}>
          {event?.stateId === StateEnum.PendingCorrection && (
            <Box>
              <Typography
                component={"h5"}
                variant="h5"
                sx={{
                  color: theme.palette.primary.main,
                  textAlign: "left",
                  paddingY: "20px",
                  fontSize: "1.75rem",
                }}
              >
                Event Declined
              </Typography>

              {isAdmin && (
                <Button variant="outlined" onClick={() => setShowCommentPopup(true)} sx={{ marginLeft: 2 }}>
                  Archive
                </Button>
              )}
              {showCommentPopup ? <CommentDialog handleCloseComments={handleCloseComments} handleAction={handleArchive} /> : <></>}

              <Grid2 container rowSpacing={2} alignItems={"center"} columnSpacing={8} sx={{ marginBottom: "32px" }}>
                <Grid2 size={"auto"}>
                  <LabeledTextField LabelIcon={CommentIcon} label="Event comments" />
                </Grid2>
                <Grid2 size={6}>
                  <TextField multiline rows={6} variant="standard" className="txt-area" sx={{ width: "100%", height: 120 }} value={event?.comment || ""} disabled />
                </Grid2>
              </Grid2>
            </Box>
          )}

          {event?.stateId === StateEnum.Archived && (
            <Box>
              <Typography
                component={"h5"}
                variant="h5"
                sx={{
                  color: theme.palette.primary.main,
                  textAlign: "left",
                  paddingY: "20px",
                  fontSize: "1.75rem",
                }}
              >
                Event Archived
              </Typography>

              <Grid2 container rowSpacing={2} alignItems={"center"} columnSpacing={8} sx={{ marginBottom: "32px" }}>
                <Grid2 size={"auto"}>
                  <LabeledTextField LabelIcon={CommentIcon} label="Event comments" />
                </Grid2>
                <Grid2 size={6}>
                  <TextField multiline rows={6} variant="standard" className="txt-area" sx={{ width: "100%", height: 120 }} value={event?.comment || ""} disabled />
                </Grid2>
              </Grid2>
            </Box>
          )}

          {event?.stateId === StateEnum.Cancelled && (
            <Grid2 container rowSpacing={2} alignItems={"center"} columnSpacing={8} sx={{ marginBottom: "32px" }}>
              <Grid2 size={"auto"}>
                <LabeledTextField LabelIcon={CommentIcon} label="Cancellation reason" />
              </Grid2>
              <Grid2 size={6}>
                <TextField multiline rows={6} variant="standard" className="txt-area" sx={{ width: "100%", height: 120 }} value={event.reason || ""} disabled />
              </Grid2>
            </Grid2>
          )}

          <Grid2 container alignItems={"flex-start"} columnSpacing={8}>
            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={NumbersIcon} label="Event ID" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.id || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={PinDropIcon} label="Location" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.locationName || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={ContactMail} label="Managing Partner Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.managingPartnerName || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={EmailIcon} label="Managing Partner Email" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.managingPartnerEmail || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={ContactMail} label="Requestor Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.requestorName || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={EmailIcon} label="Requestor Email" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.requestorEmail || ""} disabled />
              </Grid2>

              {event?.stateId === StateEnum.PendingCorrection && (
                <>
                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={Business} label="Vendor" />
                  </Grid2>
                  <Grid2 size={7}>
                    <Button id={style.selectVendor} variant="contained" disableElevation onClick={() => setShowVendorSelectDialog(true)}>
                      Select
                    </Button>
                  </Grid2>
                </>
              )}

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={PersonIcon} label="Vendor Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.vendor?.companyName || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={RequestQuoteRoundedIcon} label="Vendor Tax Id" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event?.vendor?.taxNumber || ""} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={CalendarMonthIcon} label="Event Date" />
              </Grid2>
              <Grid2 size={7}>
                <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                  <DatePicker
                    slotProps={{ textField: { fullWidth: true } }}
                    value={dayjs(event?.date)}
                    disabled={eventDatePickerDisabled()}
                    onChange={(newValue) => {
                      setEvent((prev) => ({ ...prev, date: dayjs(newValue).toDate().toISOString() }));
                    }}
                  />
                  {isAdmin && (event?.stateId === StateEnum.WaitingApproval || event?.stateId === StateEnum.PendingCorrection || event?.stateId === StateEnum.Approved) && (
                    <SaveDateButton event={event} />
                  )}
                </Box>
              </Grid2>
            </Grid2>

            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5}>
                <LabeledTextField LabelIcon={LocationOn} label="Vendor Legal Address" />
              </Grid2>
              <Grid2 size={7}>
                <TextField multiline rows={5} variant="standard" className="txt-area" sx={{ width: "100%" }} value={event?.vendor?.legalAddress || ""} disabled />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={CreditCard} label="Vendor Payment Address" />
              </Grid2>
              <Grid2 size={7}>
                <TextField multiline rows={5} variant="standard" className="txt-area" sx={{ width: "100%" }} value={event?.vendor?.paymentAddress || ""} disabled />
              </Grid2>

              {event.stateId && event.stateId >= StateEnum.Approved && !isViewOnly && (
                <>
                  <Grid2 size={5}>
                    <LabeledTextField LabelIcon={MonetizationOnIcon} label="Additional Sales Contribution" />

                    {event.stateId === StateEnum.Approved && (
                      <Typography sx={{ color: "gray", fontWeight: "bold" }}>
                        Add additional sales brought in from event not already marked as fundraiser on receipts. SN app will automatically calculate the additional donation amount under invoice info
                        screen
                      </Typography>
                    )}
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField
                      sx={{
                        width: "100%",
                        alignSelf: "flex-start",
                      }}
                      label="Additional Sales Contribution"
                      name="salesContribution"
                      value={salesContribution}
                      disabled={event.stateId != StateEnum.Approved && event.stateId != StateEnum.PendingContributionCorrection}
                      error={!!formErrors.salesContribution}
                      helperText={formErrors.salesContribution}
                      required
                      onChange={(e) => {
                        let value = e.target.value;

                        if (!isNaN(Number(value))) {
                          if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
                            value = value.replace(/^0+/, ""); // Remove leading zeros
                          }
                          event.salesContribution = Number(value);
                          setEvent(event);
                          setSalesContribution(value);

                          handleInputValidation(e, (input) => {
                            const contributionPattern = /^0(\.0+)?$|^(0|[1-9][0-9]*)(\.[0-9]+)?$/;
                            return !contributionPattern.test(input) ? "Incorrect contribution format! Field should be 0 or a valid positive number" : "";
                          });
                        }
                      }}
                    />

                    {event.stateId == StateEnum.Approved && salesContribution === "" && (
                      <span
                        style={{
                          color: theme.palette.primary.main,
                          alignSelf: "flex-start",
                          display: "block",
                          marginTop: "4px",
                        }}
                      >
                        Please enter an additional contribution amount or zero
                      </span>
                    )}
                  </Grid2>

                  <Grid2 size={5}>
                    <LabeledTextField LabelIcon={MonetizationOnIcon} label="Additional Donation Contribution" />

                    {event.stateId == StateEnum.Approved && (
                      <Typography sx={{ color: "gray", fontWeight: "bold" }}>
                        Leave as 0 even if you entered additional sales. Only enter additional donation if you plan on donating beyond fundraiser sales
                      </Typography>
                    )}
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField
                      sx={{
                        width: "100%",
                        alignSelf: "flex-start",
                      }}
                      label="Additional Donation Contribution"
                      name="donationContribution"
                      value={donationContribution}
                      disabled={event.stateId != StateEnum.Approved && event.stateId != StateEnum.PendingContributionCorrection}
                      error={!!formErrors.donationContribution}
                      helperText={formErrors.donationContribution}
                      required
                      onChange={(e) => {
                        let value = e.target.value;

                        if (!isNaN(Number(value))) {
                          if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
                            value = value.replace(/^0+/, ""); //remove leading zeros
                          }
                          event.donationContribution = Number(value);
                          setEvent(event);
                          setDonationContribution(value);

                          handleInputValidation(e, (input) => {
                            const contributionPattern = /^0(\.0+)?$|^(0|[1-9][0-9]*)(\.[0-9]+)?$/;
                            return !contributionPattern.test(input) ? "Incorrect contribution format! Field should be 0 or a valid positive number" : "";
                          });
                        }
                      }}
                    />

                    {event.stateId == StateEnum.Approved && donationContribution === "" && (
                      <span
                        style={{
                          color: theme.palette.primary.main,
                          alignSelf: "flex-start",
                          display: "block",
                          marginTop: "4px",
                        }}
                      >
                        Please enter an additional contribution amount or zero
                      </span>
                    )}
                  </Grid2>
                </>
              )}
            </Grid2>
          </Grid2>
          {showVendorSelectDialog && <VendorSelectionDialog {...{ handleClose, handleVendorSelected }} />}
        </CustomTabPanel>

        {/* panel 2 */}
        <CustomTabPanel value={value} index={1}>
          <Grid2 container alignItems={"flex-start"} columnSpacing={8}>
            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={MonetizationOnIcon} label="Additional Sales Contribution" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={"$" + event?.salesContribution || "$0"} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={MonetizationOnIcon} label="Additional Donation Contribution" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={"$" + event?.donationContribution || "$0"} disabled />
              </Grid2>

              {(event.stateId == StateEnum.Invoiced || event.stateId == StateEnum.Paid) && (
                <>
                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={DescriptionIcon} label="Invoice Number" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`${editedInvoice?.invoiceNumber}`} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={AttachMoneyIcon} label="Invoice Total" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`$${parseFloat(editedInvoice?.amount || "0").toFixed(2)}`} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={CalendarMonthIcon} label="Invoice Generation Date" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`${dayjs.utc(editedInvoice?.generationDate).local().format("MM/DD/YYYY, hh:mm:ss A")}`} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={LocationOn} label="Invoice Address" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField multiline rows={4} variant="standard" sx={{ width: "100%" }} value={vendor?.legalAddress || "Fetching..."} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={SignalWifiStatusbar4BarIcon} label="Invoice Status" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`${editedInvoice?.status}`} disabled />
                  </Grid2>
                </>
              )}
            </Grid2>

            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={ReceiptIcon} label="Total Sales" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={"$" + reportTotal || "$0"} disabled />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={VolunteerActivismIcon} label="Total Donation" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={`$${donationTotal?.toFixed(2)}`} disabled />
              </Grid2>

              {event?.stateId === StateEnum.Paid && (
                <>
                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={NumbersIcon} label="Check number" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`${editedInvoice?.checkId}`} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={InsertInvitationIcon} label="Paid Date" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`${dayjs(editedInvoice?.paidDate).format("MM/DD/YYYY")}`} disabled />
                  </Grid2>

                  <Grid2 size={5} alignContent={"center"}>
                    <LabeledTextField LabelIcon={PointOfSaleIcon} label="Paid Total" />
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField sx={{ width: "100%" }} value={`$${editedInvoice?.paidTotal?.toFixed(2)}`} disabled />
                  </Grid2>
                </>
              )}
            </Grid2>
          </Grid2>
        </CustomTabPanel>

        {/* panel 3 */}
        <CustomTabPanel value={value} index={2}>
          <LoadingIndicator show={isLoadingComments}>
            <div style={{ width: "750px" }}>
              <Grid2 container justifyContent={"space-between"}>
                <Grid2 size={12}>
                  <TextField
                    multiline
                    rows={4}
                    variant="standard"
                    className="txt-area"
                    label="Add a Comment"
                    value={newMessageText}
                    onChange={(ev) => {
                      setNewMessageText(ev.target.value);
                    }}
                    sx={{ width: "100%" }}
                  />
                </Grid2>
                <Grid2 size={8}></Grid2>
                <Grid2 size={4} sx={{ textAlign: "right", marginTop: 2 }}>
                  <RedButton
                    onClick={() => {
                      handleSendMessage();
                    }}
                    disabled={newMessageText.length === 0}
                    text={"Send"}
                  />
                </Grid2>
              </Grid2>
              <div>
                {messages.map((message, index) => (
                  <div className={styleStepper.discussionCommentContainer} key={index}>
                    <DiscussionComment message={message} />
                  </div>
                ))}
              </div>
            </div>
          </LoadingIndicator>
        </CustomTabPanel>
        {/* panel 4 */}
        <CustomTabPanel value={value} index={3}>
          <LoadingIndicator show={isLoadingAuditTrail}>
            <div id={styles.eventsGrid} className="ag-theme-quartz">
              <AgGridReact
                columnDefs={colsDef}
                rowData={auditTrail}
                loadingOverlayComponent={GridLoadingOverlay}
                ref={gridRef}
                pagination
                paginationPageSize={20}
                defaultColDef={{
                  headerClass: "tableHeader",
                  filter: "agTextColumnFilter",
                  filterParams: {
                    buttons: ["apply", "clear"],
                    closeOnApply: true,
                    maxNumConditions: 4,
                  },
                }}
                onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
                getRowId={(params) => `${params.data.id}`}
                onFirstDataRendered={onInitialDataRender}
              />
            </div>
          </LoadingIndicator>
        </CustomTabPanel>
      </Box>
    </>
  );
}
